import { supabase } from '../supabase.ts';

/**
 * Vehicles and the tax configuration.
 *
 * A vehicle is paired 1:1 with a `type = 'vehicle'` location -- that row is
 * what inventory in transit is held against, so creating a vehicle creates
 * both and they are deactivated together.
 */

export interface VehicleRecord {
  id: string;
  locationId: string | null;
  name: string;
  make: string | null;
  model: string | null;
  year: number | null;
  color: string | null;
  licensePlate: string | null;
  vin: string | null;
  insuranceExpiresAt: string | null;
  registrationExpiresAt: string | null;
  capacityValueCapCents: number;
  active: boolean;
}

/** California's transit value cap; every state that has one uses $10,000. */
export const DEFAULT_VALUE_CAP_CENTS = 1_000_000;

/** DMV VINs are 11-17 characters and never use I, O or Q. */
export const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{11,17}$/i;

export async function listVehicles(accountId: string): Promise<VehicleRecord[]> {
  const { data, error } = await supabase
    .from('vehicles')
    .select(
      'id, location_id, make, model, year, color, license_plate, vin, insurance_expires_at, registration_expires_at, capacity_value_cap_cents, active, locations(name)',
    )
    .eq('account_id', accountId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return (
    (data ?? []) as unknown as Array<{
      id: string;
      location_id: string | null;
      make: string | null;
      model: string | null;
      year: number | null;
      color: string | null;
      license_plate: string | null;
      vin: string | null;
      insurance_expires_at: string | null;
      registration_expires_at: string | null;
      capacity_value_cap_cents: number;
      active: boolean;
      locations: { name: string } | null;
    }>
  ).map((row) => ({
    id: row.id,
    locationId: row.location_id,
    name:
      row.locations?.name ?? [row.year, row.make, row.model].filter(Boolean).join(' ') ?? 'Vehicle',
    make: row.make,
    model: row.model,
    year: row.year,
    color: row.color,
    licensePlate: row.license_plate,
    vin: row.vin,
    insuranceExpiresAt: row.insurance_expires_at,
    registrationExpiresAt: row.registration_expires_at,
    capacityValueCapCents: row.capacity_value_cap_cents,
    active: row.active,
  }));
}

export interface VehicleInput {
  name: string;
  make: string | null;
  model: string | null;
  year: number | null;
  color: string | null;
  licensePlate: string | null;
  vin: string | null;
  insuranceExpiresAt: string | null;
  registrationExpiresAt: string | null;
  capacityValueCapCents: number;
  active: boolean;
}

function vehicleRow(input: VehicleInput, locationId: string | null) {
  return {
    location_id: locationId,
    make: input.make,
    model: input.model,
    year: input.year,
    color: input.color,
    license_plate: input.licensePlate,
    vin: input.vin,
    insurance_expires_at: input.insuranceExpiresAt,
    registration_expires_at: input.registrationExpiresAt,
    capacity_value_cap_cents: input.capacityValueCapCents,
    active: input.active,
  };
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40) || `vehicle-${Date.now()}`
  );
}

export async function createVehicle(accountId: string, input: VehicleInput): Promise<void> {
  // The paired location first: the vehicle row points at it, and stock in
  // transit is held against that location rather than the vehicle.
  const { data: location, error: locationError } = await supabase
    .from('locations')
    .insert({
      account_id: accountId,
      name: input.name,
      slug: slugify(input.name),
      type: 'vehicle',
      active: input.active,
      allows_delivery: true,
      allows_pickup: false,
    })
    .select('id')
    .single();

  if (locationError) throw locationError;

  const { error } = await supabase
    .from('vehicles')
    .insert({ account_id: accountId, ...vehicleRow(input, location.id) });

  if (error) throw error;
}

export async function updateVehicle(
  accountId: string,
  vehicle: VehicleRecord,
  input: VehicleInput,
): Promise<void> {
  const { error } = await supabase
    .from('vehicles')
    .update(vehicleRow(input, vehicle.locationId))
    .eq('account_id', accountId)
    .eq('id', vehicle.id);

  if (error) throw error;

  if (vehicle.locationId) {
    // The pair is one thing to an operator: renaming or parking the vehicle
    // has to move its location too, or the dispatch board keeps offering it.
    const { error: locationError } = await supabase
      .from('locations')
      .update({ name: input.name, active: input.active })
      .eq('account_id', accountId)
      .eq('id', vehicle.locationId);

    if (locationError) throw locationError;
  }
}

/** Open driver sessions block deactivation: somebody is out in it. */
export async function vehicleInUse(accountId: string, vehicleId: string): Promise<number> {
  const { count, error } = await supabase
    .from('driver_sessions')
    .select('id', { count: 'exact', head: true })
    .eq('account_id', accountId)
    .eq('vehicle_id', vehicleId)
    .is('ended_at', null);

  if (error) throw error;

  return count ?? 0;
}

export async function deleteVehicle(accountId: string, vehicle: VehicleRecord): Promise<void> {
  const { error } = await supabase
    .from('vehicles')
    .delete()
    .eq('account_id', accountId)
    .eq('id', vehicle.id);

  if (error) throw error;

  if (vehicle.locationId) {
    const { error: locationError } = await supabase
      .from('locations')
      .delete()
      .eq('account_id', accountId)
      .eq('id', vehicle.locationId);

    if (locationError) throw locationError;
  }
}

/* -------------------------------------------------------------------------- */
/* Taxes                                                                       */
/* -------------------------------------------------------------------------- */

export interface TaxSettings {
  cannabisExciseTaxBps: number;
  pricesIncludeExciseTax: boolean;
  pricesIncludeCannabisTax: boolean;
  pricesIncludeSalesTax: boolean;
  roundingMode: 'half_up' | 'bankers';
}

export const DEFAULT_TAX_SETTINGS: TaxSettings = {
  cannabisExciseTaxBps: 1500,
  pricesIncludeExciseTax: true,
  pricesIncludeCannabisTax: true,
  pricesIncludeSalesTax: true,
  roundingMode: 'half_up',
};

export interface JurisdictionRecord {
  id: string;
  name: string;
  salesTaxBps: number;
  cannabisTaxBps: number;
  medicalCannabisTaxBps: number;
  notes: string | null;
  active: boolean;
}

/** 1234 bps -> "12.34". Percent is what an operator reads off a rate card. */
export function bpsToPercent(bps: number): string {
  return (bps / 100).toFixed(2).replace(/\.00$/, '');
}

export function percentToBps(percent: string): number {
  const value = Number(percent);

  return Number.isFinite(value) ? Math.round(value * 100) : 0;
}

export async function getTaxSettings(accountId: string): Promise<TaxSettings> {
  const { data, error } = await supabase
    .from('account_tax_settings')
    .select(
      'cannabis_excise_tax_bps, prices_include_excise_tax, prices_include_cannabis_tax, prices_include_sales_tax, rounding_mode',
    )
    .eq('account_id', accountId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return DEFAULT_TAX_SETTINGS;

  return {
    cannabisExciseTaxBps: data.cannabis_excise_tax_bps,
    pricesIncludeExciseTax: data.prices_include_excise_tax,
    pricesIncludeCannabisTax: data.prices_include_cannabis_tax,
    pricesIncludeSalesTax: data.prices_include_sales_tax,
    roundingMode: data.rounding_mode === 'bankers' ? 'bankers' : 'half_up',
  };
}

/**
 * Update, then insert if there was no row.
 *
 * Deliberately not an upsert: `ON CONFLICT DO UPDATE` is checked against the
 * TABLE's update privilege, and this table grants UPDATE column by column --
 * so the upsert answered "permission denied for table account_tax_settings"
 * for a member who is perfectly entitled to change every column in it.
 */
export async function saveTaxSettings(accountId: string, values: TaxSettings): Promise<void> {
  const row = {
    cannabis_excise_tax_bps: values.cannabisExciseTaxBps,
    prices_include_excise_tax: values.pricesIncludeExciseTax,
    prices_include_cannabis_tax: values.pricesIncludeCannabisTax,
    prices_include_sales_tax: values.pricesIncludeSalesTax,
    rounding_mode: values.roundingMode,
  };

  const { data, error } = await supabase
    .from('account_tax_settings')
    .update(row)
    .eq('account_id', accountId)
    .select('account_id');

  if (error) throw error;
  if (data?.length) return;

  const { error: insertError } = await supabase
    .from('account_tax_settings')
    .insert({ account_id: accountId, ...row });

  if (insertError) throw insertError;
}

export async function listJurisdictions(accountId: string): Promise<JurisdictionRecord[]> {
  const { data, error } = await supabase
    .from('tax_jurisdictions')
    .select('id, name, sales_tax_bps, cannabis_tax_bps, medical_cannabis_tax_bps, notes, active')
    .eq('account_id', accountId)
    .order('name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    salesTaxBps: row.sales_tax_bps,
    cannabisTaxBps: row.cannabis_tax_bps,
    medicalCannabisTaxBps: row.medical_cannabis_tax_bps,
    notes: row.notes,
    active: row.active,
  }));
}

export async function saveJurisdiction(
  accountId: string,
  input: Omit<JurisdictionRecord, 'id'> & { id?: string },
): Promise<void> {
  const row = {
    name: input.name,
    sales_tax_bps: input.salesTaxBps,
    cannabis_tax_bps: input.cannabisTaxBps,
    medical_cannabis_tax_bps: input.medicalCannabisTaxBps,
    notes: input.notes,
    active: input.active,
  };

  const { error } = input.id
    ? await supabase
        .from('tax_jurisdictions')
        .update(row)
        .eq('account_id', accountId)
        .eq('id', input.id)
    : await supabase.from('tax_jurisdictions').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteJurisdiction(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('tax_jurisdictions')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}
